import { useContext, useEffect, useMemo } from 'react';
import { Box, LinearProgress } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';
import { isEmpty, isNil } from 'lodash';
import { VALID_PAGE_TYPES_TO_EXPORT } from 'common/constants/pageTypes';
import { useStore } from 'common/store';
import { LayoutContextValues, UseStoreValues } from 'common/types/store';
import { ActionButtons, Breadcrumbs, ExtraActionsMenu, HorizontalNavigation } from 'components';
import { NotificationBell } from 'components/NotificationCenter';
import LayoutContext from 'context/LayoutContext';
import { ExcelExportDialogV2 as ExcelExportDialog } from 'layouts/Main/components/ExcelExportDialogV2';
import { useGetAllocationValuesByCompany } from 'services/hooks/companies';
import { useAllocationValuesStore } from 'store';
import { getBooleanValue, getStringValue } from 'utilities';
import { Title, ValueCards } from './components';
import { ProgressBar, StyledAppBar, StyledBoxContainer, StyledToolbar, TitleContainer } from './styles';
import { HeaderProps } from './types';
import BulkActionsDrawer from '../../layouts/Main/components/BulkActionsLedger/BulkActionsDrawer';

const Header = (props: HeaderProps) => {
  const { children } = props;
  const {
    currentMeasurementDate,
    extraPageActions,
    navItems,
    openExportDialog,
    openBulkActionsDialog,
    toggleBulkActionsDialog,
    pageActions,
    pageBreadcrumbs,
    pageTitle,
    pageType,
    toggleExportDialog,
  } = useContext(LayoutContext) as LayoutContextValues;

  const [storeValue] = useStore() as unknown as UseStoreValues;
  const { companyInfo, isShowLoadingProgress } = storeValue;
  const setCurrentAllocationId = useAllocationValuesStore(state => state.setCurrentAllocationId);
  const currentAllocationVersion = useAllocationValuesStore(state => state.currentAllocationVersion);

  const isValidCompanyMeasurementDate = useMemo(
    () => currentMeasurementDate?.company_id === companyInfo?.id,
    [companyInfo, currentMeasurementDate]
  );

  const { data: allocationValues } = useGetAllocationValuesByCompany({
    company: companyInfo,
    measurementDate: currentMeasurementDate,
    version: currentAllocationVersion,
    shouldQueryAutomatically: !!isValidCompanyMeasurementDate,
  });

  useEffect(() => {
    if (!isNil(allocationValues?.id)) {
      setCurrentAllocationId?.(allocationValues?.id);
    }
  }, [allocationValues, setCurrentAllocationId]);

  return (
    <>
      <StyledAppBar color="inherit" elevation={1} position="fixed">
        <StyledToolbar>
          <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
            {/* Title and Breadcrumbs */}
            <TitleContainer>
              {/* Title */}
              {pageTitle ? <Title pageTitle={pageTitle} /> : <Skeleton height={50} variant="text" width={550} />}
              {/* BreadCrumbs */}
              {pageBreadcrumbs && <Breadcrumbs />}
            </TitleContainer>

            {/* Values Cards and Filters */}
            <Box>
              <StyledBoxContainer>
                <ValueCards company={companyInfo} measurementDate={currentMeasurementDate} />
                {/* Filters */}
                {children}
                <Box sx={{ mr: 1 }}>
                  <NotificationBell />
                </Box>
              </StyledBoxContainer>
            </Box>
          </Box>
        </StyledToolbar>

        {/* Navigation */}
        {!isEmpty(navItems) && (
          <HorizontalNavigation>
            <>
              {pageActions && <ActionButtons />}
              {extraPageActions && <ExtraActionsMenu />}
            </>
          </HorizontalNavigation>
        )}
      </StyledAppBar>

      {/* Loading Progress Bar */}
      {!isEmpty(navItems) && <ProgressBar>{isShowLoadingProgress && <LinearProgress color="primary" />}</ProgressBar>}

      {/* Excel Export */}
      {VALID_PAGE_TYPES_TO_EXPORT.includes(getStringValue(pageType)) && (
        <ExcelExportDialog onClose={toggleExportDialog as () => void} open={getBooleanValue(openExportDialog)} />
      )}
      <BulkActionsDrawer open={openBulkActionsDialog} onClose={toggleBulkActionsDialog} />
    </>
  );
};

export default Header;
